/**
 * Email campaigns, the pure half (PRD v2 §3 — "email-campaigns-lite"). One-shot broadcasts
 * to a shop's OWN clients. No sequences, no drips, no rented lists, no open tracking.
 *
 * Everything here is law rather than preference, because a broadcast is the one thing in
 * this product a shop can do that is regulated. CAN-SPAM (15 U.S.C. §7704) requires a
 * commercial email to carry the sender's real physical postal address and a working way to
 * opt out, and requires opt-outs to be honored. So the footer is BUILT, never typed — a
 * shop cannot forget it, reword it, or delete it — and the blocker list below refuses the
 * send outright when the law's inputs are missing.
 *
 * The alternative (a warning the owner can click past) puts a $53,088-per-email penalty one
 * dismissed banner away from a shop that has no lawyer. Refusing is the kinder product.
 */

import { SEND_OUTCOMES, type SendOutcome } from './delivery';

export const CAMPAIGN_STATES = ['draft', 'sending', 'sent'] as const;
export type CampaignState = (typeof CAMPAIGN_STATES)[number];

export const CAMPAIGN_AUDIENCES = ['all', 'recent', 'lapsed', 'lead_source'] as const;
export type CampaignAudience = (typeof CAMPAIGN_AUDIENCES)[number];

/**
 * 'skipped' is a real outcome (no email on file, unsubscribed) — never a silent failure.
 *
 * The same three words as `invoice_reminders` and `quote_reminders`, and now literally the
 * same const: all three tables build their CHECK from `SEND_OUTCOMES`, so a fourth word
 * cannot be added to one of them and typecheck its way into a constraint violation.
 */
export const CAMPAIGN_RECIPIENT_STATUSES = SEND_OUTCOMES;
export type CampaignRecipientStatus = SendOutcome;

/** The audience choices, in the shop's words. `param` is months for recent/lapsed. */
export function audienceLabel(audience: CampaignAudience, param: string | null): string {
  const months = Number(param) || 12;
  switch (audience) {
    case 'all':
      return 'Everyone with an email address';
    case 'recent':
      return `Customers we've worked for in the last ${months} months`;
    case 'lapsed':
      return `Customers we haven't worked for in ${months}+ months`;
    case 'lead_source':
      return param ? `Customers who found us via "${param}"` : 'By how they found us';
  }
}

// ── Merge fields ─────────────────────────────────────────────────────────────

/** The only two. More would need a picker, and a picker is how a broadcast gets hard. */
export const MERGE_FIELDS = ['customer', 'shop'] as const;
export type MergeField = (typeof MERGE_FIELDS)[number];

const MERGE_RE = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;

/** Substitute {{customer}} / {{shop}}. Unknown fields are left alone — see below. */
export function renderMerge(text: string, values: Record<MergeField, string>): string {
  return text.replace(MERGE_RE, (whole, name: string) => {
    const key = name.toLowerCase() as MergeField;
    return (MERGE_FIELDS as readonly string[]).includes(key) ? values[key] : whole;
  });
}

/**
 * Every {{thing}} in the text that isn't a real merge field. These are NOT quietly stripped:
 * a literal "Hi {{name}}," arriving in 400 inboxes is the single most visible way this
 * feature can embarrass a shop, so it's surfaced in the preview and it blocks the send.
 */
export function unknownMergeFields(...texts: string[]): string[] {
  const out = new Set<string>();
  for (const text of texts) {
    for (const m of text.matchAll(MERGE_RE)) {
      const key = (m[1] ?? '').toLowerCase();
      if (!(MERGE_FIELDS as readonly string[]).includes(key)) out.add(m[1] ?? '');
    }
  }
  return [...out];
}

// ── The footer the law requires ──────────────────────────────────────────────

export interface FooterInput {
  shopName: string;
  /** The shop's real, physical postal address. Not optional — that's the point. */
  postalAddress: string;
  unsubscribeUrl: string;
}

/**
 * The three lines the law asks of a commercial email, in the order the FTC's own guidance lists
 * them: why you got this, who sent it and where they physically are, and how to stop. ONE copy:
 * the old campaigns' footer and the promotional customer emails' (a review request, a service
 * reminder; MAP 249 ticket 06, `customerEmailFooter`) both read it.
 */
export function canSpamLines(input: FooterInput): string[] {
  return [
    `You're receiving this because you're a customer of ${input.shopName}.`,
    input.postalAddress,
    `Don't want these? Unsubscribe: ${input.unsubscribeUrl}`,
  ];
}

/**
 * Appended to every campaign email, built from the shop's own record: the law's three lines
 * (`canSpamLines`) under a rule.
 */
export function campaignFooter(input: FooterInput): string {
  return ['', '—', ...canSpamLines(input)].join('\n');
}

/** Subject + body + footer, merged — the exact bytes one recipient gets. */
export function renderCampaignEmail(input: {
  subject: string;
  body: string;
  shopName: string;
  customerName: string;
  postalAddress: string;
  unsubscribeUrl: string;
}): { subject: string; text: string } {
  const values: Record<MergeField, string> = {
    customer: input.customerName,
    shop: input.shopName,
  };
  return {
    subject: renderMerge(input.subject, values),
    text:
      renderMerge(input.body, values) +
      campaignFooter({
        shopName: input.shopName,
        postalAddress: input.postalAddress,
        unsubscribeUrl: input.unsubscribeUrl,
      }),
  };
}

// ── Can this be sent at all? ─────────────────────────────────────────────────

export interface CampaignReadiness {
  subject: string;
  body: string;
  postalAddress: string | null;
  emailConfigured: boolean;
  audienceCount: number;
  alreadySent: boolean;
}

/**
 * Everything standing between this draft and the send button, in plain sentences. An empty
 * array means go. Each one is a refusal rather than a warning — see the module note.
 */
export function campaignBlockers(r: CampaignReadiness): string[] {
  const out: string[] = [];
  if (r.alreadySent)
    out.push('This one has already gone out. Make a new message instead.');
  if (!r.subject.trim()) out.push('It needs a subject line.');
  if (!r.body.trim()) out.push('It needs something to say.');
  if (!r.postalAddress?.trim()) {
    out.push(
      "Add your shop's mailing address in Settings. The law requires a real address in every message like this, so we won't send one without it.",
    );
  }
  const unknown = unknownMergeFields(r.subject, r.body);
  if (unknown.length) {
    out.push(
      `We don't know what to put for ${unknown.map((u) => `{{${u}}}`).join(', ')} — it would go out exactly like that. Use {{customer}} or {{shop}}, or take it out.`,
    );
  }
  if (r.audienceCount === 0) {
    out.push('Nobody in this group has an email address on file.');
  }
  if (!r.emailConfigured) {
    out.push(
      "Email isn't switched on for this account yet, so nothing would actually arrive.",
    );
  }
  return out;
}
